import React from 'react'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import classNames from 'classnames'

import { type CrossClientAction, CrossClientActionPhases } from '../../../../models/CrossClient'
import { CRM_ACTION_REGISTRY, type CrmActionKind, resolveCrmActionKind } from '../../Actions/crmActionRegistry'
import styles from './CrossClientActionGroupButton.module.scss'

interface CrossClientActionGroupButtonProps {
  actions: CrossClientAction[]
  clientId: string
  clientName: string
}

const ACTION_TIMESTAMP_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
})

const formatActionTimestamp = (action: CrossClientAction) => {
  const iso = action.finishedAt ?? action.startedAt
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return ACTION_TIMESTAMP_FORMATTER.format(date)
}

const getActionSortKey = (action: CrossClientAction) => action.finishedAt ?? action.startedAt

export const CrossClientActionGroupButton: React.FC<CrossClientActionGroupButtonProps> = ({ actions, clientId, clientName }) => {
  const uniqueKinds: CrmActionKind[] = []
  const seenKinds = new Set<CrmActionKind>()
  for (const action of actions) {
    const kind = resolveCrmActionKind(action)
    if (!kind || seenKinds.has(kind)) {
      continue
    }
    seenKinds.add(kind)
    uniqueKinds.push(kind)
  }

  if (!uniqueKinds.length) {
    return null
  }

  const hasInProgress = actions.some((action) => action.phase !== CrossClientActionPhases.COMPLETED)

  const sortedActions = [...actions].sort((a, b) => getActionSortKey(b).localeCompare(getActionSortKey(a)))

  const ariaLabel = uniqueKinds.map((kind) => CRM_ACTION_REGISTRY[kind].label).join(', ')

  const popover = (
    <Popover id={`cross-client-action-popover-${clientId}`} className={styles.popover}>
      <Popover.Body>
        <div className={classNames(styles.popoverHeader, 'paragraph-medium')}>
          Actions taken on <strong>{clientName}</strong>
        </div>
        {sortedActions.map((action) => {
          const kind = resolveCrmActionKind(action)
          if (!kind) {
            return null
          }
          const descriptor = CRM_ACTION_REGISTRY[kind]
          return (
            <div key={action.id} className={styles.popoverRow}>
              <i className={classNames('bi', descriptor.icon)} />
              <div className='d-flex flex-column'>
                <span className={classNames(styles.popoverRowTitle, 'paragraph-medium')}>
                  {action.phase === CrossClientActionPhases.COMPLETED ? descriptor.completedLabel : descriptor.inProgressLabel}
                </span>
                <span className={classNames(styles.popoverRowTime, 'paragraph-small')}>{formatActionTimestamp(action)}</span>
              </div>
            </div>
          )
        })}
      </Popover.Body>
    </Popover>
  )

  return (
    <OverlayTrigger trigger='click' rootClose placement='bottom-start' overlay={popover}>
      <button type='button' className={styles.groupButton} aria-label={ariaLabel}>
        {uniqueKinds.map((kind) => (
          <i key={kind} className={classNames('bi', CRM_ACTION_REGISTRY[kind].icon)} />
        ))}
        {hasInProgress ? <span className={styles.statusDot} aria-hidden='true' /> : null}
      </button>
    </OverlayTrigger>
  )
}
